/** Copyright 2024 SAP SE or an SAP affiliate company. All rights reserved. */
import { UserDto } from '@/commons/dtos';
import { useAppSelector } from '@/hooks';
import { useTranslations } from '@/hooks/useTranslations';
import { getAribaSkuNameById } from '@/utils/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Dialog, FlexBox, Form, FormItem, Input, Label, Option, Select, Text } from '@ui5/webcomponents-react';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import './CreateUserFormDialog.css';
import { getNameSchema, userNameRegex } from './user.schema';

type Props = {
    onClose: VoidFunction;
    users?: UserDto[];
    defaultValues?: UserDto;

    onSubmit: (userDto: UserDto) => void;
};

export default function CreateUserFormDialog({ users = [], onClose, onSubmit, defaultValues }: Props) {
    const { t } = useTranslations();
    const runId = useAppSelector(state => state.productList.currentRunId);
    const { currentControlType, currentSKU } = useAppSelector(state => state.productList);
    const isEditMode = !!defaultValues;
    const skuName = useMemo(() => (currentSKU.SKUId ? getAribaSkuNameById(currentSKU.SKUId) : null), [currentSKU.SKUId]);

    const getDuplicationValidatorForField = useCallback(
        (field: keyof UserDto, value: string, users: UserDto[]) =>
            !users.some(user => {
                const isUnchanged = user[field] === defaultValues?.[field];

                if (isEditMode && isUnchanged) {
                    return false;
                }
                return user[field] === value;
            }),
        []
    );

    const getSchema = useCallback(
        (users: UserDto[]) =>
            z.object({
                givenName: getNameSchema(t('FIRST_NAME'), t),
                familyName: getNameSchema(t('LAST_NAME'), t),
                userName: z
                    .string()
                    .min(1, 'INVALID_USER_NAME_MESSAGE')
                    .max(255)
                    .regex(userNameRegex, t('NAME_CONTAINS_INVALID_CHAR', t('USER_NAME_LABEL')))
                    .refine(value => getDuplicationValidatorForField('userName', value, users), 'CREATE_USER_FORM_DUPLICATE_USERNAME_MESSAGE'),
                emailId: z
                    .string()
                    .min(1, 'INVALID_EMAIL_MESSAGE')
                    .email()
                    .refine(value => getDuplicationValidatorForField('emailId', value, users), 'CREATE_EMAIL_FORM_DUPLICATE_EMAIL_MESSAGE'),
                role: z.string(),
            }),
        []
    );

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<UserDto>({
        defaultValues: defaultValues ?? {
            givenName: '',
            familyName: '',
            userName: '',
            emailId: '',
            role: currentControlType.controlTypeOptions?.[0]?.value,
        },
        mode: 'onBlur',
        resolver: zodResolver(getSchema(users)),
    });

    const submitHandler = handleSubmit(data => {
        onSubmit({ ...data, isAnonymized: false, runId, id: defaultValues?.id });
        onClose();
    }, console.error);

    const headerText = t(isEditMode ? 'EDIT_USER_FORM_DIALOG_TITLE' : 'CREATE_USER_FORM_DIALOG_TITLE');

    return (
        <Dialog
            open
            onClose={onClose}
            headerText={headerText}
            style={{ width: 680 }}
            className="create-user-form-dialog"
            footer={
                <FlexBox style={{ gap: 8, width: '100%' }} justifyContent="End" alignItems="Center">
                    <Button data-testid="saveBtn" design="Emphasized" type="Submit" onClick={submitHandler}>
                        {t('SAVE_BUTTON')}
                    </Button>
                    <Button onClick={onClose}>{t('CANCEL_BUTTON')}</Button>
                </FlexBox>
            }
        >
            <Form layout="M2" labelSpan="M12">
                <FormItem labelContent={<Label required>{t('FIRST_NAME_LABEL')}</Label>}>
                    <Input
                        required
                        aria-required
                        aria-label="First Name"
                        valueState={errors.givenName ? 'Negative' : 'None'}
                        valueStateMessage={<Text>{t(errors.givenName?.message)}</Text>}
                        {...register('givenName')}
                    />
                </FormItem>

                <FormItem labelContent={<Label required>{t('LAST_NAME_LABEL')}</Label>}>
                    <Input
                        required
                        aria-required
                        aria-label="Last Name"
                        valueState={errors.familyName ? 'Negative' : 'None'}
                        valueStateMessage={<Text>{t(errors.familyName?.message)}</Text>}
                        {...register('familyName')}
                    />
                </FormItem>

                <FormItem labelContent={<Label required>{t('USER_NAME_LABEL')}</Label>}>
                    <Input
                        required
                        aria-label="Username"
                        aria-required
                        valueState={errors.userName ? 'Negative' : 'None'}
                        valueStateMessage={<Text>{t(errors.userName?.message)}</Text>}
                        {...register('userName')}
                    />
                </FormItem>

                <FormItem labelContent={<Label required>{t('BUSINESS_EMAIL_ID_LABEL')}</Label>}>
                    <Input
                        required
                        type="Email"
                        aria-label="Business Email"
                        aria-required
                        valueState={errors.emailId ? 'Negative' : 'None'}
                        valueStateMessage={<Text>{t(errors.emailId?.message)}</Text>}
                        {...register('emailId')}
                    />
                </FormItem>

                <FormItem labelContent={<Label required>{t('ROLE_LABEL')}</Label>}>
                    <Select required aria-required aria-label="Role" {...register('role')}>
                        {currentControlType.controlTypeOptions.map(o => {
                            const label = `${skuName ?? ''} ${t(o.translationValueKey)}`.trim();

                            return (
                                <Option value={o.value} key={o.id}>
                                    {label}
                                </Option>
                            );
                        })}
                    </Select>
                </FormItem>
            </Form>
        </Dialog>
    );
}
